import { Dispatch, SetStateAction, createContext, useState } from "react";
import {
  BentoBrandSearchParameters,
  Campaign,
  SavedBrandCollection,
  Sort,
} from "schemas/dashboard";

import { Template } from "features/Influencer/ContactList/schema";

export enum CampaignType {
  savedList = "savedList",
  brandSearch = "brandSearch",
  repitching = "repitching",
}

const defaultContextMissingFunction = () => {
  throw new Error("context is missing");
};

interface NewCampaignContextInterface {
  campaign: Campaign | null;
  setCampaign: Dispatch<SetStateAction<Campaign | null>>;
  selectedType: CampaignType | null;
  setSelectedType: Dispatch<SetStateAction<CampaignType | null>>;
  selectedBrandIds: Set<number>;
  setSelectedBrandIds: Dispatch<SetStateAction<Set<number>>>;
  selectAll: boolean;
  setSelectAll: Dispatch<SetStateAction<boolean>>;
  currentSearchParams: BentoBrandSearchParameters;
  setCurrentSearchParams: Dispatch<SetStateAction<BentoBrandSearchParameters>>;
  searchUrlParams: string;
  setSearchUrlParams: Dispatch<SetStateAction<string>>;
  selectedCollection: SavedBrandCollection | null;
  setSelectedCollection: Dispatch<SetStateAction<SavedBrandCollection | null>>;
  templates: Template[];
  setTemplates: Dispatch<SetStateAction<Template[]>>;
}

const defaultInterface: NewCampaignContextInterface = {
  campaign: null,
  setCampaign: defaultContextMissingFunction,
  selectedType: null,
  setSelectedType: defaultContextMissingFunction,
  selectedBrandIds: new Set<number>(),
  setSelectedBrandIds: defaultContextMissingFunction,
  selectAll: false,
  setSelectAll: defaultContextMissingFunction,
  currentSearchParams: { query: "", sort: Sort.Recommended },
  setCurrentSearchParams: defaultContextMissingFunction,
  searchUrlParams: "",
  setSearchUrlParams: defaultContextMissingFunction,
  selectedCollection: null,
  setSelectedCollection: defaultContextMissingFunction,
  templates: [],
  setTemplates: defaultContextMissingFunction,
};

const NewCampaignContext =
  createContext<NewCampaignContextInterface>(defaultInterface);

interface NewCampaignProviderProps {
  children: React.ReactNode;
}

export const MAX_CAMPAIGN_BRANDS = 250;

const NewCampaignProvider = ({ children }: NewCampaignProviderProps) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [selectedType, setSelectedType] = useState<CampaignType | null>(null);
  const [selectedBrandIds, setSelectedBrandIds] = useState<Set<number>>(
    new Set(),
  );
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [currentSearchParams, setCurrentSearchParams] =
    useState<BentoBrandSearchParameters>({
      query: "",
      sort: Sort.Recommended,
    });
  const [searchUrlParams, setSearchUrlParams] = useState<string>("");
  const [selectedCollection, setSelectedCollection] =
    useState<SavedBrandCollection | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);

  return (
    <NewCampaignContext.Provider
      value={{
        campaign,
        setCampaign,
        selectedType,
        setSelectedType,
        selectedBrandIds,
        setSelectedBrandIds,
        selectAll,
        setSelectAll,
        currentSearchParams,
        setCurrentSearchParams,
        searchUrlParams,
        setSearchUrlParams,
        selectedCollection,
        setSelectedCollection,
        templates,
        setTemplates,
      }}
    >
      {children}
    </NewCampaignContext.Provider>
  );
};

export { NewCampaignProvider, NewCampaignContext };
